import { useState, useEffect, useCallback, useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import { customersApi, ragApi } from "../lib/api";
import { toast } from "sonner";
import {
  Search, Loader2, Users, Mail, Phone, Package, MessageSquare,
  ExternalLink, RefreshCw, Database, ChevronRight, X, Sparkles,
} from "lucide-react";

/* ---- List View ---- */
const CustomersPage = () => {
  const { id } = useParams();
  if (id) return <CustomerDetail customerId={id} />;
  return <CustomerList />;
};

const CustomerList = () => {
  const [q, setQ] = useState("");
  const [qInput, setQInput] = useState("");
  const [customers, setCustomers] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [skip, setSkip] = useState(0);
  const [migrationState, setMigrationState] = useState(null);
  const LIMIT = 30;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await customersApi.search({ q, limit: LIMIT, skip });
      setCustomers(r.data.customers || []);
      setTotal(r.data.total || 0);
    } catch { toast.error("Failed to load customers"); }
    finally { setLoading(false); }
  }, [q, skip]);

  useEffect(() => { load(); }, [load]);

  const loadMigration = useCallback(async () => {
    try {
      const r = await customersApi.migrationStatus();
      setMigrationState(r.data);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    loadMigration();
    const id = setInterval(loadMigration, 10000);
    return () => clearInterval(id);
  }, [loadMigration]);

  const runLeadsMigration = async () => {
    try {
      await customersApi.migrateFromLeads();
      toast.success("Leads migration started");
      loadMigration();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const runZohoMigration = async () => {
    if (!window.confirm("Zoho migration can take 30-60 min. Start now?")) return;
    try {
      await customersApi.migrateFromZoho();
      toast.success("Zoho migration started in background");
      loadMigration();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const runWatiContactsMigration = async () => {
    if (!window.confirm("WATI contacts sync (both accounts) can take 30-60 min. Start now?")) return;
    try {
      await customersApi.migrateFromWatiContacts("both");
      toast.success("WATI contacts sync started in background");
      loadMigration();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const onSearchSubmit = (e) => { e.preventDefault(); setSkip(0); setQ(qInput.trim()); };
  const onClear = () => { setQInput(""); setQ(""); setSkip(0); };

  // Memoise the running-jobs subset — used twice in the JSX (gate + map).
  const runningJobs = useMemo(
    () => (migrationState?.jobs || []).filter((j) => j.status === "running"),
    [migrationState?.jobs]
  );

  return (
    <div className="max-w-6xl mx-auto" data-testid="customers-page">
      <div className="flex items-start justify-between mb-4 flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-extrabold text-[--text-primary] flex items-center gap-2" style={{ fontFamily: "Outfit, sans-serif" }}>
            <Users className="text-[--accent]" size={22} />
            Unified Customers
          </h1>
          <p className="text-xs text-[--text-muted] mt-1">
            {migrationState ? `${migrationState.total_customers.toLocaleString()} customers` : "—"}
            {" · "}merged from WATI + Zoho by phone, email, and order #
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={loadMigration} className="btn-secondary text-xs p-2" title="Refresh">
            <RefreshCw size={12} />
          </button>
          <button onClick={runLeadsMigration} className="btn-secondary text-xs flex items-center gap-1" data-testid="migrate-leads-btn">
            <Database size={12} /> Sync WATI Leads
          </button>
          <button onClick={runWatiContactsMigration} className="btn-secondary text-xs flex items-center gap-1" data-testid="migrate-wati-contacts-btn">
            <Database size={12} /> Sync WATI Contacts (200k)
          </button>
          <button onClick={runZohoMigration} className="btn-primary text-xs flex items-center gap-1" data-testid="migrate-zoho-btn">
            <Database size={12} /> Sync Zoho Tickets
          </button>
        </div>
      </div>

      {/* Migration status */}
      {runningJobs.length > 0 && (
        <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
          <p className="text-xs font-bold text-blue-900 mb-1">Migration in progress…</p>
          {runningJobs.map((j) => (
            <div key={j.job} className="text-[11px] text-blue-800" data-testid={`migration-${j.job}`}>
              <b>{j.job}</b>: {j.progress?.toLocaleString()} / {j.total?.toLocaleString() || "?"}
              <div className="h-1 bg-blue-200 rounded-full mt-1 overflow-hidden">
                <div className="h-full bg-blue-600 transition-all" style={{ width: j.total ? `${Math.min(100, (j.progress / j.total) * 100)}%` : "10%" }} />
              </div>
            </div>
          ))}
        </div>
      )}

      {/* AI Training Panel */}
      <AITrainingPanel />

      {/* Search */}
      <form onSubmit={onSearchSubmit} className="relative mb-4">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[--text-muted]" />
        <input value={qInput} onChange={(e) => setQInput(e.target.value)}
          placeholder="Search by phone, email, name, or order #..."
          className="w-full pl-10 pr-10 py-2.5 text-sm border-2 border-[--border] rounded-xl outline-none focus:border-[--accent-soft]"
          data-testid="customers-search" />
        {qInput && <button type="button" onClick={onClear} className="absolute right-3 top-1/2 -translate-y-1/2 text-[--text-muted] hover:text-[--accent]"><X size={14} /></button>}
      </form>

      {/* Results */}
      <p className="text-[11px] text-[--text-muted] mb-2">{total.toLocaleString()} matches</p>
      {loading ? (
        <div className="flex items-center justify-center py-12"><Loader2 className="animate-spin text-[--text-muted]" /></div>
      ) : customers.length === 0 ? (
        <div className="text-center py-12 text-sm text-[--text-muted]">No customers found</div>
      ) : (
        <div className="space-y-1.5">
          {customers.map((c) => <CustomerRow key={c.id} customer={c} />)}
        </div>
      )}

      {/* Pagination */}
      {total > LIMIT && (
        <div className="flex justify-center gap-2 mt-6">
          <button onClick={() => setSkip(Math.max(0, skip - LIMIT))} disabled={skip === 0} className="btn-secondary text-xs disabled:opacity-40">← Prev</button>
          <span className="text-xs text-[--text-muted] self-center">Page {Math.floor(skip / LIMIT) + 1} / {Math.ceil(total / LIMIT)}</span>
          <button onClick={() => setSkip(skip + LIMIT)} disabled={skip + LIMIT >= total} className="btn-secondary text-xs disabled:opacity-40">Next →</button>
        </div>
      )}
    </div>
  );
};

const CustomerRow = ({ customer }) => {
  const primaryPhone = customer.phones?.[0];
  const primaryEmail = customer.emails?.[0];
  const sources = Object.keys(customer.sources || {});
  return (
    <Link to={`/customers/${customer.id}`} className="flex items-center gap-3 p-3 bg-white border border-[--border] rounded-lg hover:border-[--accent-soft] transition-colors" data-testid={`customer-row-${customer.id}`}>
      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-orange-200 to-pink-200 flex items-center justify-center font-extrabold text-orange-800 flex-shrink-0">
        {(customer.name || "?")[0]?.toUpperCase()}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-bold text-[--text-primary] truncate">{customer.name || "(unnamed)"}</p>
        <div className="flex items-center gap-3 text-[11px] text-[--text-muted] flex-wrap">
          {primaryPhone && <span className="inline-flex items-center gap-0.5"><Phone size={10} /> +{primaryPhone}</span>}
          {primaryEmail && <span className="inline-flex items-center gap-0.5 truncate max-w-[200px]"><Mail size={10} /> {primaryEmail}</span>}
          {customer.order_numbers?.length > 0 && <span className="inline-flex items-center gap-0.5"><Package size={10} /> {customer.order_numbers.length} order{customer.order_numbers.length > 1 ? "s" : ""}</span>}
        </div>
      </div>
      <div className="flex items-center gap-1 flex-shrink-0">
        {sources.includes("wati_india") && <span className="text-[9px] font-bold bg-green-100 text-green-700 border border-green-200 rounded-full px-1.5 py-0.5">WATI IN</span>}
        {sources.includes("wati_us") && <span className="text-[9px] font-bold bg-blue-100 text-blue-700 border border-blue-200 rounded-full px-1.5 py-0.5">WATI US</span>}
        {sources.includes("zoho") && <span className="text-[9px] font-bold bg-amber-100 text-amber-700 border border-amber-200 rounded-full px-1.5 py-0.5">ZOHO</span>}
      </div>
      <ChevronRight size={14} className="text-[--text-muted] flex-shrink-0" />
    </Link>
  );
};

/* ---- Detail View ---- */
const CustomerDetail = ({ customerId }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const r = await customersApi.detail(customerId);
        if (!cancelled) setData(r.data);
      } catch { toast.error("Failed to load customer"); }
      finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [customerId]);

  if (loading) return <div className="flex items-center justify-center py-20"><Loader2 className="animate-spin text-[--text-muted]" /></div>;
  if (!data) return <div className="text-center py-20 text-[--text-muted]">Customer not found</div>;

  const { customer, leads, tickets } = data;
  return (
    <div className="max-w-5xl mx-auto" data-testid="customer-detail">
      <Link to="/customers" className="text-xs text-[--accent] hover:underline mb-3 inline-block">← All customers</Link>

      {/* Profile header */}
      <div className="bg-gradient-to-r from-orange-50 to-pink-50 border border-orange-200 rounded-2xl p-5 mb-5">
        <div className="flex items-center gap-4">
          <div className="w-16 h-16 rounded-full bg-gradient-to-br from-orange-300 to-pink-400 flex items-center justify-center font-extrabold text-white text-2xl">
            {(customer.name || "?")[0]?.toUpperCase()}
          </div>
          <div className="flex-1">
            <h1 className="text-2xl font-extrabold text-[--text-primary]" style={{ fontFamily: "Outfit, sans-serif" }}>
              {customer.name || "(unnamed customer)"}
            </h1>
            <p className="text-xs text-[--text-muted] mt-1">
              First seen {customer.first_seen?.slice(0, 10)} · Last seen {customer.last_seen?.slice(0, 10)}
            </p>
          </div>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mt-5">
          <InfoPill icon={Phone} label="Phones" items={customer.phones} prefix="+" />
          <InfoPill icon={Mail} label="Emails" items={customer.emails} />
          <InfoPill icon={Package} label="Orders" items={customer.order_numbers} prefix="#" />
          <InfoPill icon={Users} label="Sources" items={Object.keys(customer.sources || {}).map((s) => s.toUpperCase().replace("_", " "))} />
        </div>
      </div>

      {/* Timeline */}
      <TimelineSection customerId={customerId} />

      {/* Linked WATI chats */}
      <Section title={`WhatsApp Chats (${leads.length})`} icon={MessageSquare}>
        {leads.length === 0 ? (
          <p className="text-sm text-[--text-muted] italic">No WhatsApp chats</p>
        ) : (
          <div className="space-y-1.5">
            {leads.map((l) => (
              <Link key={l.id} to={`/chats?lead=${l.id}`} className="flex items-center gap-3 p-3 bg-white border border-[--border] rounded-lg hover:border-[--accent-soft]" data-testid={`linked-chat-${l.id}`}>
                <MessageSquare size={14} className="text-emerald-600" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-[--text-primary] truncate">{l.name || l.phone}</p>
                  <p className="text-[11px] text-[--text-muted]">+{l.phone} · {l.status} · {l.source}</p>
                </div>
                <ExternalLink size={12} className="text-[--text-muted]" />
              </Link>
            ))}
          </div>
        )}
      </Section>

      {/* Zoho tickets */}
      <Section title={`Zoho Tickets (${tickets.length})`} icon={Mail}>
        {tickets.length === 0 ? (
          <p className="text-sm text-[--text-muted] italic">No Zoho tickets</p>
        ) : (
          <div className="space-y-1.5">
            {tickets.map((t) => (
              <Link key={t.id} to={`/emails?ticket=${t.id}`} className="flex items-center gap-3 p-3 bg-white border border-[--border] rounded-lg hover:border-[--accent-soft]" data-testid={`linked-ticket-${t.id}`}>
                <Mail size={14} className="text-amber-600" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-[--text-primary] truncate">#{t.ticketNumber} · {t.subject}</p>
                  <p className="text-[11px] text-[--text-muted]">{t.status} · {t.createdTime?.slice(0, 10)}</p>
                </div>
                <ExternalLink size={12} className="text-[--text-muted]" />
              </Link>
            ))}
          </div>
        )}
      </Section>
    </div>
  );
};

const InfoPill = ({ icon: Icon, label, items, prefix = "" }) => (
  <div className="bg-white border border-[--border] rounded-lg p-2.5">
    <div className="flex items-center gap-1 text-[10px] font-bold text-[--text-muted] uppercase mb-1">
      <Icon size={10} /> {label} ({items?.length || 0})
    </div>
    {items && items.length > 0 ? (
      <div className="space-y-0.5">
        {items.slice(0, 3).map((x) => (
          <p key={x} className="text-[11px] font-medium text-[--text-primary] truncate" title={`${prefix}${x}`}>{prefix}{x}</p>
        ))}
        {items.length > 3 && <p className="text-[10px] text-[--text-muted]">+{items.length - 3} more</p>}
      </div>
    ) : (
      <p className="text-[11px] text-[--text-muted] italic">—</p>
    )}
  </div>
);

const Section = ({ title, icon: Icon, children }) => (
  <section className="mb-5">
    <h2 className="text-sm font-extrabold text-[--text-primary] mb-2 flex items-center gap-1.5" style={{ fontFamily: "Outfit, sans-serif" }}>
      <Icon size={14} className="text-[--accent]" /> {title}
    </h2>
    {children}
  </section>
);

export default CustomersPage;

/* ---- AI Training Panel: runs RAG ingestion + shows status ---- */
const AITrainingPanel = () => {
  const [state, setState] = useState(null);
  const [months, setMonths] = useState(12);

  const load = useCallback(async () => {
    try {
      const r = await ragApi.status();
      setState(r.data);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    load();
    const id = setInterval(load, 15000);
    return () => clearInterval(id);
  }, [load]);

  const trigger = async () => {
    if (!window.confirm(`Ingest last ${months} months of Zoho replies for AI learning? Runs in background (~10-30 min).`)) return;
    try {
      await ragApi.ingest(months);
      toast.success("AI training ingestion started");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const rebuild = async () => {
    try {
      const r = await ragApi.rebuild();
      toast.success(`Index rebuilt (${r.data.index_size} examples)`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const running = state?.state?.status === "running";
  const pct = state?.state?.total ? Math.min(100, (state.state.progress / state.state.total) * 100) : 0;

  return (
    <div className="mb-4 p-3 bg-gradient-to-r from-purple-50 via-pink-50 to-amber-50 border border-purple-200 rounded-lg" data-testid="ai-training-panel">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <Sparkles size={14} className="text-purple-700" />
          <div>
            <h2 className="text-xs font-extrabold text-purple-900">AI Reply Training</h2>
            <p className="text-[10px] text-purple-700">
              {state?.total_examples
                ? `${state.total_examples.toLocaleString()} past (customer → agent) pairs learned`
                : "No examples yet — ingest to teach AI your team's voice"}
              {state?.index_loaded && " · index ready"}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <select value={months} onChange={(e) => setMonths(Number(e.target.value))}
            className="px-2 py-1 text-xs border border-purple-200 rounded bg-white" data-testid="rag-months">
            <option value={1}>1 month</option>
            <option value={3}>3 months</option>
            <option value={6}>6 months</option>
            <option value={12}>12 months</option>
            <option value={24}>24 months</option>
          </select>
          <button onClick={rebuild} className="btn-secondary text-[10px] px-2 py-1" title="Rebuild index from saved examples" data-testid="rag-rebuild-btn">
            Rebuild
          </button>
          <button onClick={trigger} disabled={running}
            className="text-[10px] font-bold text-white px-3 py-1.5 rounded-lg bg-gradient-to-r from-purple-600 to-pink-600 disabled:opacity-50 flex items-center gap-1"
            data-testid="rag-ingest-btn">
            {running ? <Loader2 size={10} className="animate-spin" /> : <Sparkles size={10} />}
            {running ? "Learning…" : "Train AI"}
          </button>
        </div>
      </div>
      {running && state?.state?.total > 0 && (
        <div className="mt-2">
          <div className="h-1 bg-purple-200 rounded-full overflow-hidden">
            <div className="h-full bg-gradient-to-r from-purple-600 to-pink-600 transition-all" style={{ width: `${pct}%` }} />
          </div>
          <p className="text-[9px] text-purple-700 mt-0.5">
            Processed {state.state.progress?.toLocaleString()} pairs from {state.state.total?.toLocaleString()} tickets scanned
          </p>
        </div>
      )}
    </div>
  );
};

/* ---- Unified Timeline (chronological merged feed) ---- */

const TimelineSection = ({ customerId }) => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all"); // all | whatsapp | zoho | payment

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const r = await customersApi.timeline(customerId);
        if (!cancelled) setEvents(r.data.events || []);
      } catch { toast.error("Timeline failed"); }
      finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [customerId]);

  const filtered = useMemo(() => {
    if (filter === "all") return events;
    if (filter === "whatsapp") return events.filter((e) => e.source === "wati");
    if (filter === "zoho") return events.filter((e) => e.source === "zoho");
    if (filter === "payment") return events.filter((e) => e.source === "razorpay");
    return events;
  }, [events, filter]);

  const fmt = (ts) => {
    if (!ts) return "—";
    try {
      const d = new Date(ts);
      return d.toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" });
    } catch { return String(ts).slice(0, 16); }
  };

  return (
    <section className="mb-5" data-testid="timeline-section">
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-sm font-extrabold text-[--text-primary] flex items-center gap-1.5" style={{ fontFamily: "Outfit, sans-serif" }}>
          <ChevronRight size={14} className="text-[--accent]" />
          Unified Timeline ({events.length})
        </h2>
        <div className="flex items-center gap-1 bg-gray-100 rounded-full p-0.5 text-[10px]">
          {["all", "whatsapp", "zoho", "payment"].map((f) => (
            <button key={f} onClick={() => setFilter(f)}
              className={`px-2.5 py-1 rounded-full font-bold ${filter === f ? "bg-white shadow-sm text-[--accent]" : "text-[--text-muted]"}`}
              data-testid={`timeline-filter-${f}`}
            >{f[0].toUpperCase() + f.slice(1)}</button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-8"><Loader2 className="animate-spin text-[--text-muted]" size={20} /></div>
      ) : filtered.length === 0 ? (
        <p className="text-sm text-[--text-muted] italic py-4 text-center">No events in this view</p>
      ) : (
        <ol className="relative border-l-2 border-gray-200 ml-2 space-y-2 pb-2">
          {filtered.map((ev, i) => <TimelineEvent key={`${ev.type || "ev"}-${ev.timestamp || i}`} ev={ev} formatTime={fmt} />)}
        </ol>
      )}
    </section>
  );
};

const TimelineEvent = ({ ev, formatTime }) => {
  const KIND = {
    whatsapp_msg: { color: "bg-emerald-100 text-emerald-700 border-emerald-200", icon: MessageSquare, title: ev.direction === "outgoing" ? "You sent" : "Customer sent" },
    zoho_ticket_created: { color: "bg-amber-100 text-amber-700 border-amber-200", icon: Mail, title: `Ticket #${ev.ticket_number} opened` },
    zoho_thread_in: { color: "bg-amber-50 text-amber-700 border-amber-200", icon: Mail, title: `Customer replied (ticket #${ev.ticket_number})` },
    zoho_thread_out: { color: "bg-sky-100 text-sky-700 border-sky-200", icon: Mail, title: `${ev.author || "Agent"} replied (ticket #${ev.ticket_number})` },
    payment_link: { color: "bg-orange-100 text-orange-700 border-orange-200", icon: Package, title: `Payment link ${ev.currency || "INR"} ${ev.amount}` },
    order: { color: "bg-purple-100 text-purple-700 border-purple-200", icon: Package, title: `Order #${ev.order_number}` },
  }[ev.kind] || { color: "bg-gray-100 text-gray-700 border-gray-200", icon: Users, title: ev.kind };
  const Icon = KIND.icon;
  const isBroadcast = ev.is_broadcast;
  return (
    <li className="ml-4 relative" data-testid={`timeline-event-${ev.kind}`}>
      <span className={`absolute -left-[21px] top-1 w-4 h-4 rounded-full border-2 ${KIND.color} flex items-center justify-center`}>
        <Icon size={8} />
      </span>
      <div className="p-2 bg-white border border-[--border] rounded-lg text-[12px]">
        <div className="flex items-center justify-between mb-0.5 gap-2">
          <span className="font-bold text-[--text-primary] truncate">{KIND.title}{isBroadcast && <span className="ml-1 text-[9px] bg-purple-100 text-purple-700 px-1.5 rounded-full">BROADCAST</span>}</span>
          <span className="text-[10px] text-[--text-muted] flex-shrink-0">{formatTime(ev.ts)}</span>
        </div>
        {ev.content && <p className="text-[11px] text-[--text-secondary] line-clamp-3 whitespace-pre-wrap">{ev.content}</p>}
        {ev.subject && <p className="text-[11px] text-[--text-secondary] font-medium">{ev.subject}</p>}
        {ev.short_url && <a href={ev.short_url} target="_blank" rel="noreferrer" className="text-[10px] text-[--accent] hover:underline inline-flex items-center gap-0.5">Open link <ExternalLink size={9} /></a>}
        {ev.web_url && <a href={ev.web_url} target="_blank" rel="noreferrer" className="text-[10px] text-[--accent] hover:underline inline-flex items-center gap-0.5">View in Zoho <ExternalLink size={9} /></a>}
      </div>
    </li>
  );
};
